import { Flex, Form } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import {
    useGetEmployeeMeQuery,
    useUpdateEmployeeMutation,
} from "../../../app/features/employee/employee.api";
import { useQueryFilters } from "../../../hooks/useQueryFilters";
import UserLayout from "../../../layouts/user";
import { toast } from "../../../lib/toast";
import { cn } from "../../../utils/global.utils";
import CvInfo from "./_components/CvInfo";
import EditFormFooter from "./_components/EditFormFooter";
import EditHeader from "./_components/EditHeader";
import EducationInfo from "./_components/EducationInfo";
import ExperienceInfo from "./_components/ExperienceInfo";
import GeneralInfo from "./_components/GeneralInfo";
import PassportInfo from "./_components/PassportInfo";
import TechnicalInfo from "./_components/TechnicalInfo";
import { useProfileNavigation } from "./_hooks/useProfileNavigation";
import InfoIcon from "./_icons/InfoIcon";
import PassportImage from "./_images/passport-photo.svg";
import { type TProfileEditLink } from "./constants";

const breadcrumb = [
  {
    title: "হোম",
    path: "/",
  },
  {
    title: "ড্যাশবোর্ড",
  },
];

export default function UserProfileUpdate() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { filters, setFilterValue } = useQueryFilters<{
    type: TProfileEditLink;
  }>({
    type: "general",
  });

  const type = filters?.type || "general";
  const isCompleted = false;

  // Handle navigation
  const handleNavigate = (newType: TProfileEditLink) => {
    setFilterValue("type", newType);
  };

  // Use navigation hook
  const { canGoPrev, canGoNext, goToPrev, goToNext, goToStep } =
    useProfileNavigation({
      currentType: type,
      onNavigate: handleNavigate,
    });

  // Handle actions
  // API Hooks
  const { data: employeeData } = useGetEmployeeMeQuery();

  const [updateEmployee, { isLoading: isUpdating }] =
    useUpdateEmployeeMutation();

  // Populate form with employee data
  useEffect(() => {
    if (employeeData) {
      // Map otherDocs (type=TECHNICAL) to technical form field
      const technicalDocs =
        employeeData.otherDocs
          ?.filter((doc) => doc.type === "TECHNICAL")
          .map((doc) => ({
            name: doc.name,
            url: doc.url,
          })) || [];

      const updatedValue = {
        ...employeeData,
        dateOfBirth: employeeData.dateOfBirth
          ? dayjs(employeeData.dateOfBirth)
          : null,
        technical: technicalDocs.length > 0 ? technicalDocs : [{}],
        experiences: employeeData.experiences || [{}],
        education: employeeData.education || [{}],
        cvFileUrl: employeeData.cvFileUrl || null,
      };

      form.setFieldsValue(updatedValue);
    }
  }, [employeeData, form, type]);

  const handleCancel = () => {
    navigate("/user/profile");
  };

  const handleUpdate = () => {
    form.submit();
  };

  const onFinish = async (values: any) => {
    if (!employeeData?.id) return;

    let payload: any = {};

    switch (type) {
      case "general":
        payload = {
          fullName: values.fullName,
          fatherName: values.fatherName,
          motherName: values.motherName,
          dateOfBirth: values.dateOfBirth
            ? dayjs(values.dateOfBirth).format("YYYY-MM-DD")
            : null,
          // phone: values.phone,
          // email: values.email,
          presentAddress: values.presentAddress,
          permanentAddress: values.permanentAddress,
        };
        break;

      case "passport":
        payload = {
          passportNumber: values.passportNumber,
          passportExpireAt: values.passportExpireAt
            ? dayjs(values.passportExpireAt).format("YYYY-MM-DD")
            : null,
          passportFileUrl: values.passportFileUrl,
        };
        break;

      case "education":
        payload = {
          education: (values.education || []).map((edu: any) => ({
            ...edu,
            passingYear: edu.passingYear ? parseInt(edu.passingYear, 10) : null,
          })),
        };
        break;

      case "experience":
        payload = {
          experiences: (values.experiences || []).map((exp: any) => ({
            ...exp,
            totalYears: exp.totalYears ? parseInt(exp.totalYears, 10) : 0,
          })),
        };
        break;

      case "technical": {
        // Process technical docs
        const technicalDocs = (values.technical || []).map((item: any) => ({
          type: "TECHNICAL",
          name: item?.name,
          url: item?.url,
        }));

        // Perform non-technical docs preservation
        const otherDocs =
          employeeData.otherDocs?.filter((doc) => doc.type !== "TECHNICAL") ||
          [];

        payload = {
          otherDocs: [...otherDocs, ...technicalDocs],
        };
        break;
      }

      case "cv": {
        payload = {
          cvFileUrl: values?.cvFileUrl,
        };
        break;
      }

      default:
        break;
    }

    // Clean up undefined values if necessary, though PATCH usually ignores missing keys
    // We intentionally send null for date fields if cleared

    try {
      const res = await updateEmployee({
        id: employeeData.id,
        data: payload,
      }).unwrap();

      if (res.success) {
        toast("success", res.message || "প্রোফাইল আপডেট সফল হয়েছে");
        navigate("/user/profile");
      }
    } catch (error: any) {
      toast("error", error.data?.message || "Something went wrong");
    }
  };

  const render = () => {
    switch (type) {
      case "general":
        return <GeneralInfo />;
      case "passport":
        return <PassportInfo form={form} />;
      case "education":
        return <EducationInfo form={form} />;
      case "technical":
        return <TechnicalInfo form={form} />;
      case "experience":
        return <ExperienceInfo />;
      case "cv":
        return <CvInfo form={form} />;
      // form={form}
    }
  };

  return (
    <UserLayout title="আমার প্রোফাইল" breadcrumb={breadcrumb}>
      <div className="grid xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2">
          <EditHeader
            isCompleted={isCompleted}
            type={type}
            onStepClick={goToStep}
          />

          <div className="mt-5 border border-theme-zinc-200 rounded-xl p-6">
            {/* Your form content here */}
            <div className="mb-6">
              <Form
                className="custom-form bangla-form"
                layout="vertical"
                // requiredMark=""
                size="large"
                onFinish={onFinish}
                form={form}
              >
                {render()}
              </Form>
            </div>

            <EditFormFooter
              onCancel={handleCancel}
              onUpdate={handleUpdate}
              onPrev={goToPrev}
              onNext={goToNext}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              isLoading={isUpdating}
            />
          </div>
        </div>

        <div className={cn("hidden", type === "passport" && "block")}>
          <div className="p-4 bg-theme-yellow-50 rounded-xl border border-theme-yellow-500">
            <img src={PassportImage} className="w-full object-contain" alt="" />
            <Flex gap={8} className="mt-3">
              <InfoIcon />
              <p className="text-theme-yellow-600 text-base font-normal mt-0 flex-1">
                পাসপোর্ট এর প্রোফাইল পেইজ টি পরিষ্কার স্ক্যান/ছবি তুলে আপলোড
                করুন।
              </p>
            </Flex>
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
